import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Pagination, Rate } from "antd";
import moment from "moment";
import ReactDragListView from "react-drag-listview";

import { FaLocationDot, FaCircleMinus, FaCirclePlus } from "react-icons/fa6";
import { FaDollarSign } from "react-icons/fa";

import {
  SortButton,
  Buttons,
  Spacer,
  popover,
  boxContainer,
  inputFields,
  tables,
  checkbox,
  GetProPopup,
} from "../../components";

import { UtilityArrays, Utils, Images } from "../../config";

import {
  OthersAction,
  UniversityAction,
  SearchAction,
} from "../../store/actions";

import FilterButtons from "./filterbuttons";

import "./styles.css";

const columns = [
  {
    title: "University Name",
    dataIndex: "name",
    key: "1",
    fixed: "left",
    width: "31.25em",
    render: (text, record) => (
      <div className="flex gap-2">
        <div className="w-[15%]">
          <img
            src={record?.profilePic ?? Images.LogoIconOnly}
            className="h-[3.75em] w-[3.75em] border border-searchBorder shadow-boxContainer rounded-full object-contain bg-white"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = Images.LogoIconOnly;
            }}
          />
        </div>

        <div className="flex flex-col gap-2 w-[85%]">
          <div className="font-bold">{record.name}</div>
          <div className="flex gap-2 items-center text-[0.875em]">
            <FaLocationDot />
            <div>
              {`${record?.city ? record?.city : ""}${
                record?.state ? `, ${record?.state}` : ""
              }${record?.country?.name ? `, ${record?.country.name}` : ""}`}
            </div>
          </div>
          <div className="flex gap-2 items-center text-[0.875em]">
            <div className="pr-2 border-r border-r-gray-400">
              Average Fee:{" "}
              {record?.avgFeesUSD ? (
                <popover.WhitePopover
                  content={
                    <div>
                      {record?.avgFeesUSD
                        ? `USD ${record?.avgFeesUSD?.toLocaleString()}`
                        : "NA"}
                    </div>
                  }
                  placement={"top"}
                  trigger="hover"
                >
                  <Rate
                    defaultValue={
                      record?.avgFeesUSD > 0 && record?.avgFeesUSD <= 10000
                        ? 1
                        : record?.avgFeesUSD > 10000 &&
                          record?.avgFeesUSD <= 25000
                        ? 2
                        : record?.avgFeesUSD > 25000 &&
                          record?.avgFeesUSD <= 40000
                        ? 3
                        : record?.avgFeesUSD > 40000 &&
                          record?.avgFeesUSD <= 60000
                        ? 4
                        : 5
                    }
                    character={({ index = 0 }) => customIcons[index + 1]}
                    className="text-main text-[1.25em]"
                    disabled
                  />
                </popover.WhitePopover>
              ) : (
                "NA"
              )}
            </div>
            <div>
              Acceptance Rate:{" "}
              {record?.acceptanceRate
                ? `${(record?.acceptanceRate * 100).toFixed(1)}%`
                : "NA"}
            </div>
          </div>
          <div className="flex gap-2 items-center text-[0.875em]">
            <div className="pr-2 border-r border-r-gray-400">
              Average Course Duration: {record.courseDuration}{" "}
              {record?.courseDuration
                ? record?.courseDuration?.includes("Years")
                  ? null
                  : "Years"
                : "NA"}
            </div>
            <div>
              Application Status:{" "}
              {record?.applicationStatus === "NA" ? (
                "NA"
              ) : (
                <span
                  className={`${
                    record?.applicationStatus === "closed"
                      ? "text-red-500"
                      : "text-green-500"
                  }  font-bold`}
                >
                  {record?.applicationStatus === "closed" ? "CLOSED" : "OPEN"}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    ),
  },
  // All other columns starting from here:
  {
    title: "QS World Ranking",
    dataIndex: "qs",
    key: "2",
    width: 170,
    align: "center",
    render: (text, record) => (
      <div>{record?.rankings?.QS ? `#${record?.rankings?.QS}` : "NA"}</div>
    ),
  },
  {
    title: "Average Fee",
    dataIndex: "avgFee",
    key: "4",
    width: 150,
    align: "center",
    render: (text, record) => (
      <div>
        {record?.avgFeesUSD
          ? `USD ${record?.avgFeesUSD?.toLocaleString()}`
          : "NA"}
      </div>
    ),
  },
  {
    title: "University Type",
    dataIndex: "type",
    key: "5",
    width: 170,
    align: "center",
    render: (text, record) => <div>{record?.Type?.type ?? "NA"}</div>,
  },
  {
    title: "ACT Midrange",
    dataIndex: "actmirange",
    key: "6",
    width: 150,
    align: "center",
    render: (text, record) => (
      <div>{record?.testScores?.ACTMidrange ?? "NA"}</div>
    ),
  },
  {
    title: "SAT Midrange",
    dataIndex: "satmirange",
    key: "7",
    width: 150,
    align: "center",
    render: (text, record) => (
      <div>{record?.testScores?.SATMidrange ?? "NA"}</div>
    ),
  },

  {
    title: "IELTS Requirement",
    dataIndex: "ielts",
    key: "8",
    width: 180,
    align: "center",
    render: (text, record) => <div>{record?.requirements?.IELTS ?? "NA"}</div>,
  },
  {
    title: "TOEFL Requirement",
    dataIndex: "toefl",
    key: "9",
    width: 180,
    align: "center",
    render: (text, record) => <div>{record?.requirements?.TOEFL ?? "NA"}</div>,
  },
  {
    title: "Application Deadline",
    dataIndex: "appDeadline",
    key: "10",
    width: 180,
    align: "center",
    render: (text, record) => (
      <div className="flex flex-col">
        <div className="font-bold rounded-md bg-liteBoxBg border border-main">
          {record?.deadlines?.length > 0
            ? record?.deadlines[0]?.appDeadline
              ? moment(record?.deadlines[0]?.appDeadline).format(
                  "DD MMMM, YYYY"
                )
              : "NA"
            : "NA"}
        </div>
      </div>
    ),
  },
  {
    title: (
      <>
        SAT 25<sup>th</sup> Percentile
      </>
    ),
    dataIndex: "sat25",
    key: "11",
    width: 170,
    align: "center",
    render: (text, record) => (
      <div>{record?.testScores?.SATEnglish25Percentile ?? "NA"}</div>
    ),
  },

  {
    title: (
      <>
        SAT 75<sup>th</sup> Percentile
      </>
    ),
    dataIndex: "sat75",
    key: "12",
    width: 170,
    align: "center",
    render: (text, record) => (
      <div>{record?.testScores?.SATEnglish75Percentile ?? "NA"}</div>
    ),
  },

  {
    title: "Times Ranking",
    dataIndex: "timesRanking",
    key: "13",
    width: 140,
    align: "center",
    render: (text, record) => (
      <div>
        {record?.rankings?.TIMES ? `#${record?.rankings?.TIMES}` : "NA"}
      </div>
    ),
  },
  {
    title: "CWUR Ranking",
    dataIndex: "cwurRanking",
    key: "14",
    width: 140,
    align: "center",
    render: (text, record) => (
      <div>{record?.rankings?.CWUR ? `#${record?.rankings?.CWUR}` : "NA"}</div>
    ),
  },
  {
    title: "% Student Receiving Aid",
    dataIndex: "receiveAid",
    key: "15",
    width: 200,
    align: "center",
    render: (text, record) => (
      <div>
        {record?.percStudentsReceivingAid
          ? `${(parseFloat(record?.percStudentsReceivingAid) * 100).toFixed(
              2
            )}%`
          : "NA"}
      </div>
    ),
  },
  {
    title: "Average Financial Aid Granted Per Year",
    dataIndex: "avgAid",
    key: "16",
    width: 250,
    align: "center",
    render: (text, record) => (
      <div>
        {record?.avgFinancialAid
          ? Utils.numWithCommas(record?.avgFinancialAid)
          : "NA"}
      </div>
    ),
  },
  {
    title: "US Region",
    dataIndex: "usRegion",
    key: "18",
    width: 130,
    align: "center",
    render: (text, record) => (
      <div>{record?.country?.name === "USA" ? "Yes" : "No"}</div>
    ),
  },
  {
    title: "Research Output",
    dataIndex: "researchOutput",
    key: "19",
    width: 150,
    align: "center",
    render: (text, record) => <div>{record?.researchOutput ?? "NA"}</div>,
  },
];

const defaultCheckedList = ["2", "3", "4", "5", "6", "7", "8", "9", "10"];

const customIcons = {
  1: <FaDollarSign />,
  2: <FaDollarSign />,
  3: <FaDollarSign />,
  4: <FaDollarSign />,
  5: <FaDollarSign />,
};

const UniversityLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const viewMode = location.pathname.split("/")[2];
  const fixedColumns = [columns[0]];

  const profileData = useSelector((state) => state?.profile?.user);
  const { universities, isLoading, count } = useSelector(
    (state) => state.university
  );
  const { uniSearch } = useSelector((state) => state.search);

  const [hoveredColIndex, setHoveredColIndex] = useState(null);
  const [checkedList, setCheckedList] = useState(defaultCheckedList);
  const [dragColumns, setDragColumns] = useState(
    columns.slice(1).filter((col) => checkedList.includes(col.key))
  );
  const [gridView, setGridView] = useState(viewMode === "grid");
  const [budgetMin, setBudgetMin] = useState(null);
  const [budgetMax, setBudgetMax] = useState(null);
  const [pageNo, setPageNo] = useState(uniSearch?.pageNo || 1);
  const [filters, setFilters] = useState({
    limit: uniSearch?.limit || 10,
    offset: uniSearch?.offset || 0,
    search: uniSearch?.search || "",
    region: uniSearch?.region || [],
    areaOfStudy: uniSearch?.areaOfStudy || [],
    majors: uniSearch?.majors || [],
    budget: uniSearch?.budget || "",
    scholarships: uniSearch?.scholarships || "",
    exchangeAvailibility: uniSearch?.exchangeAvailibility || "",
    halalFood: uniSearch?.halalFood || "",
    locationType: uniSearch?.locationType || "",
    tag: uniSearch?.tag || [],
  });

  useEffect(() => {
    setDragColumns(
      columns.slice(1).filter((col) => checkedList.includes(col.key))
    );
  }, [checkedList]);

  useEffect(() => {
    if (budgetMin || budgetMax) {
      handleFilterChange({
        budget: `${budgetMin}-${budgetMax}`,
      });
    }
  }, [budgetMin, budgetMax]);

  useEffect(() => {
    const budget = uniSearch?.budget || "";
    const matchingValue = UtilityArrays?.budgetValues.find(
      (item) => item.value === budget
    );

    if (!matchingValue && budget) {
      const [min, max] = budget.split("-").map(Number);
      setBudgetMin(min || null);
      setBudgetMax(max || null);
    } else {
      setBudgetMin(null);
      setBudgetMax(null);
    }
  }, [uniSearch]);

  useEffect(() => {
    handleSearch();
  }, [uniSearch]);

  useEffect(() => {
    dispatch(
      SearchAction.uniSearch({
        ...filters,
        pageNo,
      })
    );
  }, [filters, pageNo]);

  useEffect(() => {
    dispatch(
      OthersAction.getPrograms({
        programTypes: JSON.stringify(filters.areaOfStudy),
      })
    );
  }, [filters?.areaOfStudy]);

  const handleSearch = async () => {
    const payload = {
      ...uniSearch,
      region: JSON.stringify(uniSearch.region),
      majors: JSON.stringify(uniSearch.majors),
      areaOfStudy: JSON.stringify(uniSearch.areaOfStudy),
      tag: JSON.stringify(uniSearch.tag),
    };
    dispatch(UniversityAction.getUniversities(payload));
  };

  const onShowSizeChange = (current, pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      limit: pageSize,
      offset: (current - 1) * pageSize,
    }));
    setPageNo(current);
  };

  const handlePageChange = (pageNumber) => {
    setPageNo(pageNumber);
    setFilters((prevFilters) => ({
      ...prevFilters,
      offset: (pageNumber - 1) * prevFilters.limit,
    }));
  };

  const handleViewChange = (isGridView) => {
    setGridView(isGridView);
    navigate(isGridView ? "/universities/grid" : "/universities/list");
  };

  const renderButton = (isGridView, label) =>
    !isGridView ? (
      <Buttons.DefaultButton
        size="small"
        onClick={() => handleViewChange(isGridView)}
        title={
          label === "List View"
            ? "Click to switch to a detailed, list-style view of universities."
            : "Click to switch to a visual, grid-style layout of universities."
        }
      >
        {label}
      </Buttons.DefaultButton>
    ) : (
      <Buttons.WhiteButton
        size="small"
        onClick={() => handleViewChange(isGridView)}
        title={
          label === "List View"
            ? "Click to switch to a detailed, list-style view of universities."
            : "Click to switch to a visual, grid-style layout of universities."
        }
      >
        {label}
      </Buttons.WhiteButton>
    );

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleReset = async () => {
    setFilters({
      ...filters,
      offset: 0,
      search: "",
      region: [],
      areaOfStudy: [],
      majors: [],
      budget: "",
      scholarships: "",
      exchangeAvailibility: "",
      halalFood: "",
      locationType: "",
      tag: [],
    });
    setBudgetMin(null);
    setBudgetMax(null);
    setPageNo(1);
  };

  const handleFilterChange = (updatedFilter) => {
    setPageNo(1);
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...updatedFilter,
      offset: 0,
    }));
  };

  const handleRemoveFilter = (filterKey, value) => {
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };
      if (Array.isArray(updatedFilters[filterKey])) {
        updatedFilters[filterKey] = updatedFilters[filterKey].filter(
          (item) => item !== value
        );
      } else {
        updatedFilters[filterKey] = "";
      }
      return updatedFilters;
    });
    setPageNo(1);
  };

  const fixedCount = 2;

  const dragProps = {
    onDragEnd(fromIndex, toIndex) {
      const from = fromIndex - fixedCount;
      const to = toIndex - fixedCount;

      if (from < 0 || to < 0) return;

      const newOrder = [...dragColumns];
      const movedItem = newOrder.splice(from, 1)[0];
      newOrder.splice(to, 0, movedItem);
      setDragColumns(newOrder);
    },
    nodeSelector: "th",
    handleSelector: ".dragHandler",
  };

  const finalColumns = [
    {
      title: "#",
      dataIndex: "serial",
      key: "01",
      fixed: "left",
      width: 70,
      render: (text, record, index) => (
        <div>{(pageNo - 1) * filters.limit + index + 1}</div>
      ),
    },
    ...fixedColumns,
    ...dragColumns.map((col, index) => ({
      ...col,
      title: (
        <div className="relative">
          <span
            className="dragHandler"
            style={{ cursor: "move", userSelect: "none" }}
          >
            {col.title}
          </span>

          <popover.WhitePopover
            content={
              <checkbox.VerticalCheckbox
                value={checkedList}
                options={columns.slice(2).map(({ title, key }) => ({
                  label: title,
                  value: key,
                }))}
                onChange={(val) => setCheckedList(val)}
              />
            }
          >
            {hoveredColIndex === index && (
              <FaCirclePlus
                className="absolute -right-3 z-50 top-1/2 -translate-y-1/2 text-white h-[1.75em] w-[1.75em] cursor-pointer"
                title="Add column"
              />
            )}
          </popover.WhitePopover>
          {hoveredColIndex === index && (
            <FaCircleMinus
              className="absolute -left-3 z-50 top-1/2 -translate-y-1/2 text-red-300 h-[1.75em] w-[1.75em] cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveColumn(col.key);
              }}
              title="Remove column"
            />
          )}
        </div>
      ),

      onHeaderCell: (column) => ({
        width: column.width,
        onMouseEnter: () => setHoveredColIndex(index),
        onMouseLeave: () => setHoveredColIndex(null),
      }),
    })),
  ];

  const handleRemoveColumn = (colKey) => {
    setCheckedList((prev) => prev.filter((key) => key !== colKey));
  };

  return (
    <div className="universities">
      <div className="flex justify-center gap-2">
        <inputFields.DefaultSearchBox
          onChange={(e) => {
            setPageNo(1);
            setFilters({
              ...filters,
              offset: 0,
              search: e.target.value,
            });
          }}
          value={filters.search}
          onKeyDown={handleKeyDown}
        />
        <div className="w-[7.5em]">
          <Buttons.DefaultButton
            size="medium"
            width="full"
            style={{ height: "100%" }}
            onClick={() => handleSearch()}
          >
            Search
          </Buttons.DefaultButton>
        </div>
        <div className="w-[7.5em]">
          <Buttons.RedButton
            style={{ height: "100%" }}
            size="medium"
            width="full"
            onClick={() => navigate("/explore")}
            title="Click to discover and browse more university options."
          >
            Explore
          </Buttons.RedButton>
        </div>
      </div>
      <Spacer height={20} />

      <FilterButtons
        handleFilterChange={handleFilterChange}
        filters={filters}
        budgetMin={budgetMin}
        setBudgetMin={setBudgetMin}
        budgetMax={budgetMax}
        setBudgetMax={setBudgetMax}
        handleReset={handleReset}
        handleRemoveFilter={handleRemoveFilter}
      />

      <Spacer height={20} />
      <boxContainer.UniversityBoxContainer>
        <div className="flex justify-between border-b border-b-liteBoxBorder px-4 py-3">
          <div className="flex items-center gap-3 ">
            <div className="font-bold text-[1.25em] pl-2">
              {count.toLocaleString()} Universities
            </div>
            {renderButton(true, "Grid View")}
            {renderButton(false, "List View")}
          </div>
          {!gridView && (
            <popover.WhitePopover
              content={
                <checkbox.VerticalCheckbox
                  value={checkedList}
                  options={columns.slice(2).map(({ title, key }) => ({
                    label: title,
                    value: key,
                  }))}
                  onChange={(val) => setCheckedList(val)}
                />
              }
            >
              <SortButton title="Click to edit and personalize the displayed data points by adding or removing them as needed." />
            </popover.WhitePopover>
          )}
        </div>
        <div className="w-[calc(100vw-20em)] overflow-x-auto max-w-full relative">
          <ReactDragListView.DragColumn {...dragProps}>
            <tables.DefaultTable
              columns={finalColumns}
              data={universities}
              borderradius={0}
              rowKey={(record) => record.id}
              loading={isLoading}
              onRow={(record) => ({
                onClick: (event) => {
                  if (event.ctrlKey) {
                    window.open(`/universities/${record.id}`, "_blank");
                  } else {
                    navigate(`/universities/${record.id}`);
                  }
                },
              })}
            />
          </ReactDragListView.DragColumn>
        </div>

        <div className="flex justify-center items-center p-3">
          <Pagination
            responsive
            hideOnSinglePage={false}
            current={pageNo}
            pageSize={filters.limit}
            total={count}
            showSizeChanger
            pageSizeOptions={UtilityArrays?.pageSizes}
            onShowSizeChange={onShowSizeChange}
            onChange={handlePageChange}
          />
        </div>
      </boxContainer.UniversityBoxContainer>
    </div>
  );
};

export default UniversityLayout;
